// React related imports
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

// React Router
import { useLocation } from 'react-router-dom';

// Auth Context
import { useAuth } from './AuthProvider';

// Interfaces
import {
  UserInfo,
  UserInfoResponse,
} from '../common/interfaces/UserInfo.interface';

const UserInfoContext = createContext<{
  userInfo: UserInfo | null;
  loadingUserInfo: boolean;
}>({
  userInfo: null,
  loadingUserInfo: true,
});

const fetchUserInfo = (): Promise<UserInfo> =>
  fetch(`${process.env.REACT_APP_BACKEND_URL}/dashboard-auth/userInfo`, {
    credentials: 'include',
  })
    .then((res) => (res.ok ? res.json() : Promise.reject('Failed')))
    .then((resBody: UserInfoResponse) => resBody.data);

export const UserInfoProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loadingUserInfo, setLoadingUserInfo] = useState(true);
  const loaded = useRef(false);

  useEffect(() => {
    loaded.current = false;
    if (!isAuthenticated) {
      setUserInfo(null);
      setLoadingUserInfo(false);
      return;
    }

    // Set loading to true when we start fetching
    setLoadingUserInfo(true);

    fetchUserInfo()
      .then(setUserInfo)
      .catch(() => setUserInfo(null))
      .finally(() => {
        loaded.current = true;
        setLoadingUserInfo(false);
      });
  }, [isAuthenticated]);

  // A SuperAdmin can change this person's role at any time. Re-read it on every navigation, without a
  // loading state, so the sidebar and the page guards follow the new role from the next click.
  useEffect(() => {
    if (!isAuthenticated || !loaded.current) return;
    fetchUserInfo()
      .then(setUserInfo)
      .catch(() => undefined);
  }, [pathname]);

  return (
    <UserInfoContext.Provider value={{ userInfo, loadingUserInfo }}>
      {children}
    </UserInfoContext.Provider>
  );
};

export const useUserInfo = () => useContext(UserInfoContext);
